/***
 * Created on 2026/1/7
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useState} from 'react';
import {useTranslation} from "react-i18next";
import sys_info_svg from "../../static/img/utils/ic-sys-info--dark.svg";
import ic_lock_svg from "../../static/img/utils/ic-lock.svg";
import ModalComponent from "../common/ModalComonent/ModalComponent";
import useToggle from "../../hook/ToggleHook";
import {useAlertContext} from "../../context/AlertContext";
import {useMutation} from "@tanstack/react-query";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {useParams} from "react-router-dom";

const EXPIRATION_OPTIONS = [
    {label: 'None', value: null},
    {label: '1 day', value: 1},
    {label: '3 days', value: 3},
    {label: '7 days', value: 7},
    {label: '30 days', value: 30},
    {label: '90 days', value: 90},
    {label: '180 days', value: 180},
    {label: '1 year', value: 365},
];

function CloudSpaceUserSpaceSharingComponent({profile, isReadOnly = false}) {
    const {t} = useTranslation();
    const alert = useAlertContext();
    const {oid} = useParams();
    const {OrganizationAPI} = useOrganizationApi();
    const [sharedExternalAccess, setSharedExternalAccess] = useState(profile?.share_config?.external_access || false);
    const [shareLinkControl, setShareLinkControl] = useState(profile?.share_config?.shared_link_access_scope || 'org_members');
    const [shareLinkRequirePassword, setShareLinkRequirePassword] = useState(profile?.share_config?.require_password || false);
    const [showDisableExternalAccessModal, setShowDisableExternalAccessModal] = useState(false);
    const [shareLinkRequireExpiration, setShareLinkRequireExpiration] = useState(false);
    const [expirationOption, setExpirationOption] = useState(() => EXPIRATION_OPTIONS.find(opt => opt.value === profile?.share_config?.max_ttl) || EXPIRATION_OPTIONS[0] );
    const [open, node] = useToggle();

    const shareLinkMutation = useMutation({
        mutationFn: async (data) => {
            const operation = data.operation;
            if (operation === 'share_link_control') {
                return await OrganizationAPI.q2.patch(`/org_storage/${oid}/share_config`,null, data);
            }

            if (operation === 'external_access_control') {
                return await OrganizationAPI.q2.patch(`/org_storage/${oid}/share_config`,null, data);
            }

            if (operation === 'share_link_security') {
                return await OrganizationAPI.q2.patch(`/org_storage/${oid}/share_config`,null, data);
            }

            if (operation === 'require_expiration_option') {
                return await OrganizationAPI.q2.patch(`/org_storage/${oid}/share_config`,null, data);
            }
            return {}
        },
        onError: (error) => {
            console.error('Share link settings update error:', error);
            alert(true, 'error', 0, t('@Failed to update shared link settings.@'));
        }
    });

    const toggleSharedExternalAccessHandler = async (e) => {
        setSharedExternalAccess(prev => {
            const newValue = !prev;
            if (newValue === false) {
                setShowDisableExternalAccessModal(true);
                return prev;
            }
            else {
                return shareLinkMutation.mutateAsync({
                    operation: 'external_access_control',
                    external_access: true
                }).then(() => {
                    alert(true, 'success', 0, t('@External sharing for Shared Spaces is now enabled.@'), 10000);
                    return newValue;
                }).catch(() => {/* handled in mutation*/});
            }
        });
    };

    const shareLinkControlChangeHandler = async (option) => {
        if (option === shareLinkControl) return;

        try {
            await shareLinkMutation.mutateAsync({shared_link_access_scope: option, operation: 'share_link_control'});
            setShareLinkControl(option);
            if (option !== 'anyone') {
                alert(true, 'success', 0, t('@Security settings hidden for internal-only links.@'), 10000);
            }
            else {
                alert(true, 'success', 0, t('@Shared link access updated.@'), 10000);
            }
        }
        catch (error) {/* handled in mutation*/}
    };

    const toggleShareLinkRequirePasswordHandler = async  (e) => {
        try {
            await shareLinkMutation.mutateAsync({operation: "share_link_security", require_password: !shareLinkRequirePassword});
            setShareLinkRequirePassword(prev => !prev);
            alert(true, 'success', 0, t('@Shared link access updated.@'), 10000);
        }
        catch (error) {/* handled in mutation*/}
    }

    // const toggleShareLinkRequireExpirationHandler = (e) => {
    //     setShareLinkRequireExpiration(prev => !prev);
    // }

    const expirationOptionChangeHandler = (option) => {
        setExpirationOption(prev => {
            shareLinkMutation.mutateAsync({
                operation: 'require_expiration_option',
                max_ttl: option.value
            }).then(() => {
                alert(true, 'success', 0, t('@Share Links max expiration days has been updated.@'), 10000);
                return option.value;
            }).catch(() => {/* handled in mutation*/});
            return option
        });
    }

    return (
        <>
            <div className="section-title-bar !pb-10">
                <div className="col-label">{t('@Sharing@')}</div>
                <div className="fs15 color-dark-gray mt-8">{t('@Manage external sharing policies and link security.@')}</div>
            </div>
            <div className="section-content indent-l-15">
                <div className="form-group-wrapper">
                    <div className="form-group">
                        <div className="form-row form-row--head">
                            <div className="form-title">{t('@Shared Space External Access@')}</div>
                        </div>
                        <div className="form-row form-row--direction-column">
                            <div className="form-row--section">
                                <div className="display-flex align-items-center flex-gap-10 mb-4">
                                    <div>{t('@Allow sharing Shared Spaces with external users@')}
                                        <span className="display-inline-block w-16px h-1lh ml-8 mr-8 vertical-align-middle tooltip">
                                            <img className="w-100" src={sys_info_svg} />
                                            <span className="tooltiptext">{t('@External users must sign in with a QNAP ID. They do not consume license seats.@')}</span>
                                        </span>:
                                    </div>
                                    <div className={`comp-switch ${isReadOnly ? 'disabled' : ''}`} onClick={isReadOnly ? undefined : toggleSharedExternalAccessHandler}>
                                        <div className={`q-switch switch-${sharedExternalAccess ? 'on' : 'off'}`}></div>
                                    </div>
                                </div>
                                <div className="note-msg">
                                    {t('@Allow members to invite external users to collaborate on an entire Shared Space.@')}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-group-divider !pt-10 !pr-0 !pb-10 !pl-0"></div>

                    <div className="form-group">
                        <div className="form-row form-row--head">
                            <div className="form-title">{t('@Shared Link Control@')}</div>
                        </div>
                        <div className="form-row form-row--direction-column">
                            <div>{t('@Who can access shared links@')}</div>
                            <div className="note-msg">
                                {t('@This setting controls who can open shared links created by members for files and folders.@')}
                            </div>
                        </div>
                        <div className="form-row form-row--direction-column">
                            <div className="form-row--section">
                                <div className="comp-radio-wrapper">
                                    <div className="comp-radio-group">
                                        <div className="comp-radio">
                                            <input type="radio" id="rb-1" name="shared-link-control" checked={shareLinkControl==='anyone'} onChange={e=>shareLinkControlChangeHandler("anyone")} disabled={isReadOnly}/>
                                            <label htmlFor="rb-1" className="comp-label"></label>
                                        </div>
                                        <div className="val-field">
                                            <div className="display-block">{t('@Anyone@')}</div>
                                            <div className="display-block note-msg pt-2">{t('@Any user with the link can access (public).@')}</div>
                                        </div>
                                    </div>
                                    <div className="comp-radio-group">
                                        <div className="comp-radio">
                                            <input type="radio" id="rb-2" name="shared-link-control" checked={shareLinkControl==='invited_qids'} onChange={e=>shareLinkControlChangeHandler("invited_qids")} disabled={isReadOnly}/>
                                            <label htmlFor="rb-2" className="comp-label"></label>
                                        </div>
                                        <div className="val-field">
                                            <div className="display-block">{t('@Only invited people@')}</div>
                                            <div className="display-block note-msg pt-2">{t('@Only users explicitly added in link settings can access.@')}</div>
                                        </div>
                                    </div>
                                    <div className="comp-radio-group">
                                        <div className="comp-radio">
                                            <input type="radio" id="rb-3" name="shared-link-control" checked={shareLinkControl==="org_members"} onChange={e=>shareLinkControlChangeHandler("org_members")} disabled={isReadOnly}/>
                                            <label htmlFor="rb-3" className="comp-label"></label>
                                        </div>
                                        <div className="val-field">
                                            <div className="display-block">{t('@Organization members only@')}</div>
                                            <div className="display-block note-msg pt-2">{t('@External users are blocked even if they have the link.@')}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-group-divider !pt-10 !pr-0 !pb-10 !pl-0"></div>

                    { shareLinkControl !== 'anyone' &&
                        <div className="form-group">
                            <div className="form-row form-row--head">
                                <div className="form-title">{t('@Shared Link Security@')}</div>
                                <img src={ic_lock_svg} className="ml-8 min-w-fit-content" />
                            </div>
                            <div className="form-row form-row--direction-column">
                                <div>{t('@For links shared only within the organization, password and expiration settings are optional.@')}</div>
                            </div>
                        </div>
                    }

                    { shareLinkControl === 'anyone' &&
                        <div className="form-group">
                            <div className="form-row form-row--head">
                                <div className="form-title">{t('@Shared Link Security@')}</div>
                            </div>
                            <div className="form-row form-row--direction-column">
                                <div>{t('@Who can access shared links@')}</div>
                                <div className="note-msg">
                                    {t('@This setting controls who can open shared links created by members for files and folders.@')}
                                </div>
                            </div>
                            <div className="form-row form-row--direction-column">
                                <div className="form-row--section">
                                    <div className="mb-24">
                                        <div className="display-flex align-items-center flex-gap-10 mb-4">
                                            <div>{t('@Require password for shared links:@')}</div>
                                            <div className={`comp-switch ${isReadOnly ? 'disabled' : ''}`} onClick={isReadOnly ? undefined : toggleShareLinkRequirePasswordHandler}>
                                                <div className={`q-switch switch-${shareLinkRequirePassword ? 'on' : 'off'}`}></div>
                                            </div>
                                        </div>
                                        <div className="note-msg">
                                            {t('@Members must set a password when creating a shared link.@')}
                                        </div>
                                    </div>
                                    <div className="mb-8">
                                        <div className="display-flex align-items-center flex-gap-10 mb-4">
                                            <div>{t('@Require expiration for shared links:@')}</div>
                                            {/*<div className={`comp-switch ${isReadOnly ? 'disabled' : ''}`} onClick={isReadOnly ? undefined : toggleShareLinkRequireExpirationHandler}>*/}
                                            {/*    <div className={`q-switch switch-${shareLinkRequireExpiration ? 'on' : 'off'}`}></div>*/}
                                            {/*</div>*/}
                                        </div>
                                        <div className="note-msg">
                                            {t('@Members must set an expiration date when creating a shared link.@')}
                                        </div>
                                    </div>
                                    <div className="indent-l-30">
                                        <div className="display-flex align-items-center flex-gap-10 mb-4">
                                            <div className="fs14">{t('@Maximum expiration:@')}</div>
                                            <div className={`comp-select comp-select--small auto-width drop-adjustable`} ref={node}>
                                                <div className="select-toggle">
                                                    <span className="select-label">{t(`@${expirationOption.label}@`)}</span>
                                                </div>
                                                <div className={`comp-dropdown comp-select-dropdown ${open}`}>
                                                    <ul className="comp-dropdown-menu">
                                                        <li className="section">
                                                            {
                                                                EXPIRATION_OPTIONS.map((option, index) => (
                                                                    <div key={index} className="section-items selection" onClick={e=>expirationOptionChangeHandler(option)}>{t(`@${option.label}@`)}</div>
                                                                ))
                                                            }
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="note-msg">
                                            {t('@Members can choose any date within this limit.@')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>

            {
                showDisableExternalAccessModal &&
                <ModalComponent title={t('@Dialog Disable external Shared Space access@')} isOpen={showDisableExternalAccessModal} onClose={e=>setShowDisableExternalAccessModal(false)} okText={t('@Disable@')} modalSize={""}
                                onSubmit={async () => {
                                    await shareLinkMutation.mutateAsync({
                                        operation: 'external_access_control',
                                        external_access: false
                                    });
                                    setSharedExternalAccess(false);
                                    setShowDisableExternalAccessModal(false);
                                    alert(true, 'success', 0, t('@All external access to Shared Spaces has been revoked.@'), 10000);
                                }}>
                    <div className="dialog-content">
                        <div className="text-wrap">
                            {t('@Turning this off will immediately revoke access for all external users from every Shared Space.@')}
                        </div>
                        <div className="text-wrap">
                            {t('@Revoked users will lose access and no longer see the space in "Shared With Me".@')}
                        </div>
                    </div>
                </ModalComponent>
            }
        </>
);
}

export default CloudSpaceUserSpaceSharingComponent;